/**
 * Finance page: template MUI components + API.
 * Table of transactions (Transactions API) with server-side pagination.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as api from '../../modules/api';
import dayjs from 'dayjs';
import PageContainer from '../mui-crud/components/PageContainer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';
import TransactionDetailDrawer from './TransactionDetailDrawer';

function formatDate(value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0, s = 0, nano = 0] = value;
    const ms = typeof nano === 'number' ? Math.round(nano / 1e6) : 0;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s, ms);
    return dayjs(date).format('DD.MM.YYYY HH:mm');
  }
  if (typeof value === 'string' && /^\d{4},\d+,\d+/.test(value)) {
    const parts = value.split(',').map(Number);
    const [y, m, d, h = 0, min = 0, s = 0, nano = 0] = parts;
    const ms = nano ? Math.round(nano / 1e6) : 0;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s, ms);
    return dayjs(date).format('DD.MM.YYYY HH:mm');
  }
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

function normalizeTransactionList(data) {
  if (!data) return [];
  const list = data.transactions ?? data.data ?? data;
  return Array.isArray(list) ? list : [];
}

function mapTransactionsToRows(list) {
  if (!Array.isArray(list)) return [];
  return list.map((tx, index) => ({
    id: tx.id ?? tx._id ?? `row-${index}`,
    description: tx.description ?? '—',
    rentId: tx.rentId ?? '—',
    rentIdShort: tx.rentId ? String(tx.rentId).slice(-4) : '—',
    createdDate: tx.createdDate ?? tx.createdAt ?? tx.date,
    createdDateDisplay: formatDate(tx.createdDate ?? tx.createdAt ?? tx.date),
    price: tx.price != null ? tx.price : '—',
    transactionStatus: tx.transactionStatus ?? '—',
    _raw: tx,
  }));
}

export default function FinancePage() {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchByRentId, setSearchByRentId] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadPage = useCallback(async (page, pageSize = 20) => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.Transactions(page, pageSize);
      const data = res?.data ?? res;
      const list = normalizeTransactionList(data);
      setRows(mapTransactionsToRows(list));
      const count = data?.transactionCount ?? data?.pageCount ?? list.length;
      setTotalCount(Number(count) ?? list.length);
    } catch (e) {
      setError(e?.message || t('finance.errorLoad'));
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (searchByRentId != null) return;
    loadPage(paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize, loadPage, searchByRentId]);

  const handleRefresh = useCallback(() => {
    if (loading) return;
    if (searchByRentId) {
      setLoading(true);
      setError(null);
      api
        .FindTransactionByRentId(searchByRentId)
        .then((res) => {
          const data = res?.data ?? res;
          const list = Array.isArray(data) ? data : data != null ? [data] : [];
          setRows(mapTransactionsToRows(list));
          setTotalCount(list.length);
        })
        .catch((e) => {
          setError(e?.message || t('finance.errorNotFound'));
          setRows([]);
          setTotalCount(0);
        })
        .finally(() => setLoading(false));
    } else {
      loadPage(paginationModel.page, paginationModel.pageSize);
    }
  }, [loading, loadPage, paginationModel.page, paginationModel.pageSize, searchByRentId, t]);

  const handleSearch = useCallback(
    (query) => {
      const q = (query ?? searchInputValue ?? '').toString().trim();
      setSearchInputValue(q);
      if (!q) {
        setSearchByRentId(null);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        loadPage(0, paginationModel.pageSize);
        return;
      }
      setError(null);
      setLoading(true);
      setSearchByRentId(q);
      api
        .FindTransactionByRentId(q)
        .then((res) => {
          const data = res?.data ?? res;
          const list = Array.isArray(data) ? data : data != null ? [data] : [];
          setRows(mapTransactionsToRows(list));
          setTotalCount(list.length);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        })
        .catch((e) => {
          setError(e?.message || t('finance.errorNotFound'));
          setRows([]);
          setTotalCount(0);
        })
        .finally(() => setLoading(false));
    },
    [searchInputValue, loadPage, paginationModel.pageSize, t],
  );

  const handleClearSearch = useCallback(() => {
    setSearchByRentId(null);
    setSearchInputValue('');
    setError(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadPage(0, paginationModel.pageSize);
  }, [loadPage, paginationModel.pageSize]);

  const handleRowClick = useCallback((params) => {
    const tx = params.row?._raw ?? params.row;
    setSelectedTransaction(tx);
    setDetailOpen(true);
  }, []);

  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: t('finance.columnId'), flex: 0.6, minWidth: 100 },
      { field: 'description', headerName: t('finance.columnDescription'), flex: 1.2, minWidth: 160 },
      { field: 'rentIdShort', headerName: t('finance.columnRentId'), flex: 0.5, minWidth: 80 },
      { field: 'createdDateDisplay', headerName: t('finance.columnDateTime'), flex: 0.9, minWidth: 140 },
      { field: 'price', headerName: t('finance.columnAmount'), flex: 0.5, minWidth: 90 },
      { field: 'transactionStatus', headerName: t('finance.columnType'), flex: 0.6, minWidth: 100 },
    ],
    [t],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('finance.title')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1}>
            {searchByRentId != null && (
              <Chip label={t('common.clearSearch')} onDelete={handleClearSearch} size="small" sx={{ mr: 0.5 }} />
            )}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={searchByRentId != null ? rows.length : (totalCount || rows.length)}
              paginationMode={searchByRentId != null ? 'client' : 'server'}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
              onRowClick={handleRowClick}
              localeText={dataGridLocaleText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
              disableColumnResize
              density="compact"
              showToolbar
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                pagination: {
                  labelRowsPerPage: t('common.rowsPerPage'),
                },
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: handleSearch,
                  searchPlaceholder: t('finance.searchPlaceholder'),
                  loading,
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
      </PageContainer>
      <TransactionDetailDrawer
        transaction={selectedTransaction}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedTransaction(null);
        }}
        onRefresh={handleRefresh}
        onTransactionUpdated={(updated) => setSelectedTransaction(updated)}
      />
    </Box>
  );
}
